import { saveDraft } from '@theme/pf-product-drafts';
import { isCartBusy } from '@theme/pf-cart-client';

// Use the complete native product form for the selected set, including its actual
// variant, price, palette, saved design and fulfillment weight. No price arithmetic.
export class NameSetPicker extends HTMLElement {
  #request;
  #generation = 0;
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    const editing = new URLSearchParams(location.search).has('pf_edit');
    for (const link of this.querySelectorAll('[data-pf-name-choice]')) {
      if (editing) link.setAttribute('aria-disabled', 'true');
    }
    this.addEventListener('click', event => {
      const link = event.target.closest('[data-pf-name-choice]');
      if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (editing || link.getAttribute('aria-pressed') === 'true') return;
      this.choose(link);
    });
    this.addEventListener('keydown', event => {
      if (event.key !== ' ' || !event.target.matches('[data-pf-name-choice]')) return;
      event.preventDefault(); event.target.click();
    });
  }
  disconnectedCallback() { this.#request?.abort(); }
  async choose(link) {
    const currentMain = this.closest('main');
    const form = currentMain?.querySelector('form[data-pf-product-form]');
    // Never replace a form during draft restore, add-to-cart or cart-line editing.
    if (!currentMain || !form || form.inert || isCartBusy() || new URLSearchParams(location.search).has('pf_edit')) return;
    const target = new URL(link.href, location.href);
    const allowed = [...this.querySelectorAll('[data-pf-name-choice]')].map(a => new URL(a.href, location.href).pathname);
    if (target.origin !== location.origin || !allowed.includes(target.pathname)) return;
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) target.searchParams.set('preview_theme_id', preview);
    this.#request?.abort();
    const controller = new AbortController(); this.#request = controller;
    const generation = ++this.#generation;
    const status = this.querySelector('[data-pf-set-status]');
    const previousInert = form.inert;
    form.inert = true;
    this.setAttribute('aria-busy', 'true');
    status.textContent = this.dataset.loading;
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      await saveDraft(form);
      const response = await fetch(target, { credentials:'same-origin', signal:controller.signal, headers:{Accept:'text/html'} });
      if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('Product unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextMain = html.querySelector('main#MainContent');
      const nextPicker = nextMain?.querySelector('pf-name-set-picker');
      const nextForm = nextMain?.querySelector('form[data-pf-product-form]');
      if (!nextMain || nextPicker?.dataset.productId !== link.dataset.pfNameChoice || nextForm?.dataset.pfProductId !== link.dataset.pfNameChoice || !nextForm.querySelector('[name="id"]')?.value) throw new Error('Product mismatch');
      if (generation !== this.#generation || !this.isConnected) return;
      // Fresh nodes ensure the per-product draft and native custom elements are
      // initialized for this product rather than retaining another form's state.
      const replacement = document.importNode(nextMain, true);
      currentMain.replaceWith(replacement);
      history.replaceState({}, '', target.href);
      document.title = html.title;
      replacement.dispatchEvent(new Event('shopify:section:load', {bubbles:true}));
      replacement.querySelector(`[data-pf-name-choice="${link.dataset.pfNameChoice}"]`)?.focus({preventScroll:true});
    } catch {
      if (generation === this.#generation && this.isConnected) status.textContent = this.dataset.error;
    } finally {
      clearTimeout(timeout);
      if (generation === this.#generation) {
        form.inert = previousInert;
        this.removeAttribute('aria-busy');
      }
    }
  }
}
if (!customElements.get('pf-name-set-picker')) customElements.define('pf-name-set-picker', NameSetPicker);

